import { useCallback, useEffect, useState } from 'react'

import { Input } from 'components/Input'
import { Textarea } from 'components/Textarea'
import { MessageAssetQueryError } from 'components/MessageAssetQueryError'
import {
  lineStrings,
  organizationFeatureStrings,
  surveySelectorStrings,
} from 'constants/strings'
import { cn } from 'utils/cn'
import type { TRichMenuAreaFieldErrors } from './validateRichMenuCreate'
import {
  ERichMenuAreaActionType,
  RICH_MENU_ACTION_LABEL_MAX_LENGTH,
  RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH,
  RICH_MENU_URI_MAX_LENGTH,
  type TRichMenuAreaAction,
} from 'types'
import {
  buildMailtoUri,
  isMailtoUri,
  MAILTO_SCHEME,
  parseMailtoUri,
  type TRichMenuMailAction,
} from 'utils/richMenu'
import { useSurveys } from 'hooks/useSurveys'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { EOrganizationFeature } from 'types/enum'
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature'

type RichMenuAreaActionsProps = {
  areaKeys: string[]
  areas: TRichMenuAreaAction[]
  onChange: (index: number, next: TRichMenuAreaAction) => void
  fieldErrors?: Record<number, TRichMenuAreaFieldErrors>
  expandAreaSignal?: { key: number; index: number } | null
  disabled?: boolean
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p className="mt-1 text-xs text-red-600" role="alert">
      {message}
    </p>
  )
}

const strings = lineStrings.management.richMenu.create

/**
 * 画面上のアクション種別。APIの型は増やさず、URIアクションのうち
 * mailto: のものを「メール」として見せ分ける（LINEにmail型は存在しないため）。
 */
type TAreaActionKind = ERichMenuAreaActionType | 'mail'

const actionOptions: TAreaActionKind[] = [
  ERichMenuAreaActionType.None,
  ERichMenuAreaActionType.Uri,
  'mail',
  ERichMenuAreaActionType.Message,
  ERichMenuAreaActionType.Survey,
]

function optionLabel(kind: TAreaActionKind): string {
  switch (kind) {
    case ERichMenuAreaActionType.None:
      return strings.actionTypeOptionNone
    case ERichMenuAreaActionType.Uri:
      return strings.actionTypeOptionUri
    case 'mail':
      return strings.actionTypeOptionMail
    case ERichMenuAreaActionType.Message:
      return strings.actionTypeOptionMessage
    case ERichMenuAreaActionType.Survey:
      return 'アンケート'
  }
}

/** 保存済みのアクションから画面上の種別を導出する（追加の状態を持たない）。 */
function actionKindOf(action: TRichMenuAreaAction): TAreaActionKind {
  if (action.type === ERichMenuAreaActionType.Uri && isMailtoUri(action.uri ?? '')) {
    return 'mail'
  }

  return action.type
}

/** 画面上の種別を切り替えたときの、初期状態のアクションを返す。 */
function emptyActionOf(kind: TAreaActionKind): TRichMenuAreaAction {
  // メールは uri を mailto: で始めておくことで、以降は prefix だけで種別を判別できる
  return kind === 'mail'
    ? { type: ERichMenuAreaActionType.Uri, uri: MAILTO_SCHEME }
    : { type: kind }
}

export function RichMenuAreaActions({
  areaKeys,
  areas,
  onChange,
  fieldErrors,
  expandAreaSignal,
  disabled = false,
}: RichMenuAreaActionsProps) {
  const [openIndex, setOpenIndex] = useState(0)
  const { data: currentUser } = useCurrentUser()
  const canUseSurvey = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.SURVEY,
  )
  const {
    surveys,
    loading: surveysLoading,
    error: surveysError,
    refetch: refetchSurveys,
  } = useSurveys({
    enabled: canFetchOrganizationFeature(currentUser, EOrganizationFeature.SURVEY),
  })
  const selectableSurveys = surveys.filter(
    (survey) =>
      survey.status === 'ACTIVE' && survey.responseFormat === 'FORM',
  )

  useEffect(() => {
    if (expandAreaSignal) {
      setOpenIndex(expandAreaSignal.index)
    }
  }, [expandAreaSignal])

  const toggle = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? -1 : index))
  }, [setOpenIndex])

  return (
    <section className="grid gap-4 rounded-2xl border border-border/60 bg-white p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">
          {strings.actionsSectionTitle}
        </h2>
      </div>

      <div className="grid gap-3">
        {areaKeys.map((key, index) => {
          const action = areas[index] ?? { type: ERichMenuAreaActionType.None }
          const isOpen = openIndex === index
          const labelLen = (action.label ?? '').length
          const errs = fieldErrors?.[index]
          const kind = actionKindOf(action)
          const mail = kind === 'mail'
            ? parseMailtoUri(action.uri ?? '') ?? {
              address: '',
              subject: '',
              body: '',
            }
            : null

          const onMailChange = (next: Partial<TRichMenuMailAction>) => {
            if (!mail) return
            onChange(index, {
              ...action,
              uri: buildMailtoUri({ ...mail, ...next }),
            })
          }

          // アンケートが無効な組織では新規の選択肢から外し、保存済みのアンケートアクションは表示を残す。
          const selectableActionOptions = canUseSurvey
            ? actionOptions
            : actionOptions.filter(
              (opt) =>
                opt !== ERichMenuAreaActionType.Survey ||
                action.type === ERichMenuAreaActionType.Survey,
            )

          const selectControl = (
            <select
              className="w-full rounded-lg border border-border/60 bg-white px-3 py-2 text-sm text-ink"
              value={kind}
              disabled={disabled}
              onChange={(e) => {
                onChange(index, emptyActionOf(e.target.value as TAreaActionKind))
              }}
            >
              {selectableActionOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {optionLabel(opt)}
                </option>
              ))}
            </select>
          )

          return (
            <div
              key={key}
              className={cn(
                'rounded-xl border bg-surface/40 p-4 transition-colors',
                isOpen
                  ? 'border-emerald-500/80 ring-1 ring-emerald-500/25'
                  : 'border-border/50',
              )}
            >
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left"
                onClick={() => toggle(index)}
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <span className="text-muted">{isOpen ? '▾' : '▸'}</span>
                  {key}
                </span>
              </button>

              {!isOpen && (
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  {selectControl}
                </div>
              )}

              {isOpen && (
                <div className="mt-4 grid gap-4">
                  <label className="grid gap-1 text-xs text-muted">
                    {strings.actionType}
                    {selectControl}
                  </label>

                  {kind === ERichMenuAreaActionType.Uri && (
                    <label className="grid gap-1 text-xs text-muted">
                      {strings.uri}
                      <Input
                        value={action.uri ?? ''}
                        disabled={disabled}
                        onChange={(e) =>
                          onChange(index, { ...action, uri: e.target.value })
                        }
                        placeholder={strings.urlPlaceholder}
                        maxLength={RICH_MENU_URI_MAX_LENGTH}
                        aria-invalid={errs?.uri ? true : undefined}
                        className={
                          errs?.uri
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                            : undefined
                        }
                      />
                      <FieldError message={errs?.uri} />
                    </label>
                  )}

                  {mail && (
                    <div className="grid gap-4">
                      <p className="text-xs leading-relaxed text-muted">
                        {strings.mailHint}
                      </p>

                      <label className="grid gap-1 text-xs text-muted">
                        {strings.mailAddress}
                        <Input
                          type="email"
                          value={mail.address}
                          disabled={disabled}
                          onChange={(e) =>
                            onMailChange({ address: e.target.value })
                          }
                          placeholder={strings.mailAddressPlaceholder}
                          aria-invalid={errs?.uri ? true : undefined}
                          className={
                            errs?.uri
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                              : undefined
                          }
                        />
                        <FieldError message={errs?.uri} />
                      </label>

                      <label className="grid gap-1 text-xs text-muted">
                        {strings.mailSubject}
                        <Input
                          value={mail.subject}
                          disabled={disabled}
                          onChange={(e) =>
                            onMailChange({ subject: e.target.value })
                          }
                          placeholder={strings.mailSubjectPlaceholder}
                          aria-invalid={errs?.mailContent ? true : undefined}
                          className={
                            errs?.mailContent
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                              : undefined
                          }
                        />
                      </label>

                      <label className="grid gap-1 text-xs text-muted">
                        {strings.mailBody}
                        <Textarea
                          rows={4}
                          value={mail.body}
                          disabled={disabled}
                          onChange={(e) =>
                            onMailChange({ body: e.target.value })
                          }
                          placeholder={strings.mailBodyPlaceholder}
                          aria-invalid={errs?.mailContent ? true : undefined}
                          className={cn(
                            'min-h-[100px] resize-y py-2',
                            errs?.mailContent &&
                            'border-red-500 focus:border-red-500 focus:ring-red-500/30',
                          )}
                        />
                      </label>

                      {/* 長さ超過は件名・本文が原因のため、宛先欄ではなくここに出す */}
                      <FieldError message={errs?.mailContent} />
                    </div>
                  )}

                  {kind === ERichMenuAreaActionType.Message && (
                    <div className="grid gap-2">
                      <p className="text-xs leading-relaxed text-muted">
                        {strings.messageTextHint}
                      </p>
                      <Textarea
                        aria-label={strings.text}
                        disabled={disabled}
                        rows={4}
                        maxLength={RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH}
                        value={action.text ?? ''}
                        onChange={(e) =>
                          onChange(index, { ...action, text: e.target.value })
                        }
                        placeholder={strings.messageTextPlaceholder}
                        aria-invalid={errs?.text ? true : undefined}
                        className={cn(
                          'min-h-[100px] resize-y py-2',
                          errs?.text &&
                          'border-red-500 focus:border-red-500 focus:ring-red-500/30',
                        )}
                      />
                      <FieldError message={errs?.text} />
                      <span className="text-right text-[11px] text-muted">
                        {(action.text ?? '').length}/
                        {RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH}
                      </span>
                    </div>
                  )}

                  {action.type === ERichMenuAreaActionType.Survey && (
                    <div className="grid gap-2">
                      {!canUseSurvey ? (
                        <p className="text-xs text-muted">
                          {organizationFeatureStrings.unavailable.replace(
                            '$feature',
                            organizationFeatureStrings.labels[
                              EOrganizationFeature.SURVEY
                            ],
                          )}
                        </p>
                      ) : surveysError ? (
                        <MessageAssetQueryError
                          message={surveySelectorStrings.loadFailed}
                          retryLabel={surveySelectorStrings.retry}
                          retryingLabel={surveySelectorStrings.retrying}
                          isRetrying={surveysLoading}
                          onRetry={() => {
                            void refetchSurveys()
                          }}
                        />
                      ) : null}
                      <label className="grid gap-1 text-xs text-muted">
                        {surveySelectorStrings.label}
                        <select
                          className={cn(
                            'w-full rounded-lg border border-border/60 bg-white px-3 py-2 text-sm text-ink',
                            errs?.surveyId &&
                              'border-red-500 focus:border-red-500 focus:ring-red-500/30',
                          )}
                          value={action.surveyId ?? ''}
                          disabled={
                            disabled || surveysLoading || Boolean(surveysError)
                          }
                          aria-invalid={errs?.surveyId ? true : undefined}
                          onChange={(e) =>
                            onChange(index, {
                              ...action,
                              surveyId: e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            })
                          }
                        >
                          <option value="">
                            {surveySelectorStrings.placeholder}
                          </option>
                          {selectableSurveys.map((survey) => (
                            <option key={survey.id} value={survey.id}>
                              {survey.name}
                            </option>
                          ))}
                        </select>
                        <FieldError message={errs?.surveyId} />
                      </label>
                    </div>
                  )}

                  {action.type === ERichMenuAreaActionType.Uri && (
                    <label className="grid gap-1 text-xs text-muted">
                      {/* メールもURIアクションのためラベル欄は共通で表示する */}
                      {strings.actionLabel}
                      <Textarea
                        rows={2}
                        disabled={disabled}
                        maxLength={RICH_MENU_ACTION_LABEL_MAX_LENGTH}
                        value={action.label ?? ''}
                        onChange={(e) =>
                          onChange(index, { ...action, label: e.target.value })
                        }
                        placeholder={strings.actionLabelPlaceholder}
                        aria-invalid={errs?.label ? true : undefined}
                        className={cn(
                          'min-h-[72px] resize-y py-2',
                          errs?.label &&
                          'border-red-500 focus:border-red-500 focus:ring-red-500/30',
                        )}
                      />
                      <FieldError message={errs?.label} />
                      <span className="text-right text-[11px] text-muted">
                        {labelLen}/{RICH_MENU_ACTION_LABEL_MAX_LENGTH}
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
